'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { CartConcessionDto } from '@cineamo/api/models/cart-concession/CartConcessionDto.types';

import { ApiErrorLike, isApiErrorResponse } from '@cineamo/api/types/ApiErrorResponse.types';

import { isReportableApiFailure } from '@cineamo/api/helpers/api-fallback';
import { setCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { CONCESSION_CATALOG_QUERY_KEY } from '@cineamo/composables/src/hooks/useConcessionCatalog';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconBin16 } from '@cineamo/ui/src/icons/ic-bin';
import { IconMinus16 } from '@cineamo/ui/src/icons/ic-minus';
import { IconPlus16 } from '@cineamo/ui/src/icons/ic-plus';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import { reportBookingError } from './booking-telemetry-client';
import { concessionErrorKey, concessionLineTotal } from './concession-helper';

const stepperButtonClass = cn(
    'flex size-36 shrink-0 items-center justify-center rounded-4 outline-none',
    'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
    'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50'
);

/**
 * A snack line of the cart, per Figma "Concessions Selection" (Cinema Websites 503:88053): name and price, and on
 * the cart step a counter whose first button removes the line at one. The snack modal only adds.
 */
export default function ConcessionCartItem({
    concession,
    isEditable
}: {
    concession: CartConcessionDto;
    isEditable: boolean;
}): React.JSX.Element {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const queryClient = useQueryClient();
    const { cartUuid, currency, concessions, setConcessions, refreshCartDto, onlineTicketingFee } = useCartProvider();
    const [isSaving, setIsSaving] = useState(false);
    // A line of a deleted product has no catalog id to send, so it cannot be changed here.
    const canEdit = isEditable && concession.cinemaProductId !== null;

    const changeQuantity = async (delta: number) => {
        setIsSaving(true);
        // The PUT replaces the whole list: every catalog line, this one changed; a quantity of 0 drops it.
        const items = concessions.flatMap(({ id, cinemaProductId, quantity }) =>
            cinemaProductId === null
                ? []
                : [{ cinemaProductId, quantity: id === concession.id ? Math.max(0, quantity + delta) : quantity }]
        );
        try {
            const response = await setCartConcessions(
                cartUuid,
                items.filter(({ quantity }) => quantity > 0)
            );
            if (!('_embedded' in response) || !response._embedded)
                throw new Error('Cart concessions response without concessions');
            setConcessions(response._embedded.concessions);
            // The cart's totals include the fee on the lines.
            await refreshCartDto();
        } catch (caught) {
            if (isReportableApiFailure(caught)) {
                reportBookingError({ step: 'concession-save', code: 'failed', error: caught, level: 'warning' });
            }
            // Not an API error: the request itself failed (network).
            const error: ApiErrorLike = isApiErrorResponse(caught) ? caught : {};
            showToast({ toastType: toast.error, messageTitle: t(`error.${concessionErrorKey(error)}`) });
            if (error.status === 400) {
                queryClient.invalidateQueries({ queryKey: [CONCESSION_CATALOG_QUERY_KEY] });
            }
        }
        setIsSaving(false);
    };

    return (
        <div
            className={cn(
                'flex items-start gap-12 rounded-4 p-8',
                'bg-[linear-gradient(180deg,var(--tw-gradient-stops))] from-[rgba(var(--on-background-color-rgb),0.05)] to-[rgba(var(--on-background-color-rgb),0.1)]'
            )}>
            <span className="min-w-0 grow text-body-bold-14 leading-[24px] tracking-[0.7px]">
                {canEdit ? concession.name : `${concession.quantity}x ${concession.name}`}
            </span>
            <div className="flex shrink-0 flex-col items-end gap-8">
                <span className="text-body-bold-14 leading-[24px] tracking-[0.7px]">
                    {formatter.number.formatCurrency(
                        concessionLineTotal(concession, onlineTicketingFee) / 100,
                        currency
                    )}
                </span>
                {canEdit && (
                    <div className="flex w-[120px] items-center rounded-4 bg-[rgba(var(--on-background-color-rgb),0.1)]">
                        <button
                            type="button"
                            disabled={isSaving}
                            aria-label={t('decrease-quantity', { name: concession.name })}
                            onClick={() => changeQuantity(-1)}
                            className={stepperButtonClass}>
                            {concession.quantity === 1 ? <IconBin16 /> : <IconMinus16 />}
                        </button>
                        <span
                            className="grow text-center text-body-14 font-montserrat-medium leading-[24px]"
                            aria-live="polite">
                            {concession.quantity}
                        </span>
                        <button
                            type="button"
                            disabled={isSaving}
                            aria-label={t('increase-quantity', { name: concession.name })}
                            onClick={() => changeQuantity(1)}
                            className={stepperButtonClass}>
                            <IconPlus16 />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
